import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Chip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Computer as TechIcon,
  Category as OtherIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { STEP_LABELS } from '../utils/constants';
import useRoadmapStore from '../store/roadmapStore';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import useAISuggest from '../hooks/useAISuggest';

const ResourceCard = ({ resource, index, onEdit, onDelete, type }) => {
  const { t } = useTranslation();
  
  const getTypeIcon = (type) => {
    switch (type) {
      case 'human': return <PeopleIcon />;
      case 'financial': return <MoneyIcon />;
      case 'technology': return <TechIcon />;
      default: return <OtherIcon />;
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {getTypeIcon(type)}
          <Typography variant="h6" component="h3" sx={{ ml: 1, flexGrow: 1 }}>
            {resource.name || 'Untitled Resource'}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {resource.description || 'No description provided'}
        </Typography>
        
        {type === 'human' && (
          <Box>
            {resource.role && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Role: {resource.role}
              </Typography>
            )}
            {resource.skills && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Skills: {resource.skills}
              </Typography>
            )}
            {resource.availability && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Availability: {resource.availability}
              </Typography>
            )}
          </Box>
        )}

        {type === 'financial' && (
          <Box>
            {resource.amount && (
              <Typography variant="h6" color="primary">
                ${resource.amount}
              </Typography>
            )}
            {resource.source && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Source: {resource.source}
              </Typography>
            )}
            {resource.category && (
              <Chip label={resource.category} size="small" variant="outlined" />
            )}
          </Box>
        )}

        {type === 'technology' && (
          <Box>
            {resource.category && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Category: {resource.category}
              </Typography>
            )}
            {resource.cost && (
              <Typography variant="body2" color="primary">
                Cost: ${resource.cost}
              </Typography>
            )}
            {resource.license && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                License: {resource.license}
              </Typography>
            )}
          </Box>
        )}

        {type === 'other' && (
          <Box>
            {resource.category && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Type: {resource.category}
              </Typography>
            )}
            {resource.cost && (
              <Typography variant="body2" color="primary">
                Cost: ${resource.cost}
              </Typography>
            )}
            {resource.availability && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Availability: {resource.availability}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
      
      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(index)}>
          {t('stepContent.resources.actions.edit')}
        </Button>
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(index)}>
          {t('stepContent.resources.actions.delete')}
        </Button>
      </CardActions>
    </Card>
  );
};

const ResourceForm = ({ resource, type, onSave, onCancel }) => {
  const { t } = useTranslation();
  const getInitialData = () => {
    const base = {
      name: resource?.name || '',
      description: resource?.description || '',
      required_date: resource?.required_date || '',
      priority: resource?.priority || 'Medium'
    };

    switch (type) {
      case 'human':
        return {
          ...base,
          role: resource?.role || '',
          skills: resource?.skills || '',
          availability: resource?.availability || '',
          contact: resource?.contact || ''
        };
      case 'financial':
        return {
          ...base,
          amount: resource?.amount || '',
          source: resource?.source || '',
          category: resource?.category || 'Budget'
        };
      case 'technology':
        return {
          ...base,
          category: resource?.category || 'Software',
          cost: resource?.cost || '',
          license: resource?.license || 'Commercial',
          vendor: resource?.vendor || ''
        };
      default:
        return {
          ...base,
          category: resource?.category || 'Equipment',
          cost: resource?.cost || '',
          availability: resource?.availability || ''
        };
    }
  };

  const [formData, setFormData] = useState(getInitialData());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave(formData);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const getTypeSpecificFields = () => {
    switch (type) {
      case 'human':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('stepContent.resources.form.fields.role')}
                value={formData.role}
                onChange={handleChange('role')}
                placeholder={t('stepContent.resources.form.placeholders.role')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('stepContent.resources.form.fields.skills')}
                value={formData.skills}
                onChange={handleChange('skills')}
                placeholder={t('stepContent.resources.form.placeholders.skills')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('stepContent.resources.form.fields.availability')}
                value={formData.availability}
                onChange={handleChange('availability')}
                placeholder={t('stepContent.resources.form.placeholders.availability')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('stepContent.resources.form.fields.contact')}
                value={formData.contact}
                onChange={handleChange('contact')}
                placeholder={t('stepContent.resources.form.placeholders.contact')}
              />
            </Grid>
          </>
        );
      
      case 'financial':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={handleChange('amount')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleChange('category')}
                  label="Category"
                >
                  <MenuItem value="Budget">Budget</MenuItem>
                  <MenuItem value="Investment">Investment</MenuItem>
                  <MenuItem value="Grant">Grant</MenuItem>
                  <MenuItem value="Loan">Loan</MenuItem>
                  <MenuItem value="Revenue">Revenue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Source"
                value={formData.source}
                onChange={handleChange('source')}
                placeholder="Where will this funding come from?"
              />
            </Grid>
          </>
        );

      case 'technology':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('stepContent.resources.form.fields.category')}</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleChange('category')}
                  label={t('stepContent.resources.form.fields.category')}
                >
                  <MenuItem value="Software">{t('stepContent.resources.form.options.category.software')}</MenuItem>
                  <MenuItem value="Hardware">{t('stepContent.resources.form.options.category.hardware')}</MenuItem>
                  <MenuItem value="Platform">{t('stepContent.resources.form.options.category.platform')}</MenuItem>
                  <MenuItem value="Tool">{t('stepContent.resources.form.options.category.tool')}</MenuItem>
                  <MenuItem value="Service">{t('stepContent.resources.form.options.category.service')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={formData.cost}
                onChange={handleChange('cost')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('stepContent.resources.form.fields.license')}</InputLabel>
                <Select
                  value={formData.license}
                  onChange={handleChange('license')}
                  label={t('stepContent.resources.form.fields.license')}
                >
                  <MenuItem value="Commercial">{t('stepContent.resources.form.options.license.commercial')}</MenuItem>
                  <MenuItem value="Open Source">{t('stepContent.resources.form.options.license.openSource')}</MenuItem>
                  <MenuItem value="Subscription">{t('stepContent.resources.form.options.license.subscription')}</MenuItem>
                  <MenuItem value="Free">{t('stepContent.resources.form.options.license.free')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vendor"
                value={formData.vendor}
                onChange={handleChange('vendor')}
                placeholder="Company or provider"
              />
            </Grid>
          </>
        );

      default:
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Type/Category"
                value={formData.category}
                onChange={handleChange('category')}
                placeholder="e.g., Equipment, Facility, Material"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={formData.cost}
                onChange={handleChange('cost')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Availability"
                value={formData.availability}
                onChange={handleChange('availability')}
                placeholder="When and how can you access this resource?"
              />
            </Grid>
          </>
        );
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {resource ? t('stepContent.resources.form.editTitle') : t('stepContent.resources.form.addTitle')}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label={t('stepContent.resources.form.fields.name')}
              value={formData.name}
              onChange={handleChange('name')}
              placeholder={t('stepContent.resources.form.placeholders.name')}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.resources.form.fields.priority')}</InputLabel>
              <Select
                value={formData.priority}
                onChange={handleChange('priority')}
                label={t('stepContent.resources.form.fields.priority')}
              >
                <MenuItem value="High">{t('common.high')}</MenuItem>
                <MenuItem value="Medium">{t('common.medium')}</MenuItem>
                <MenuItem value="Low">{t('common.low')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.resources.form.fields.description')}
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={t('stepContent.resources.form.placeholders.description')}
            />
          </Grid>
          
          {getTypeSpecificFields()}
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('stepContent.resources.form.fields.requiredDate')}
              type="date"
              value={formData.required_date}
              onChange={handleChange('required_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
              sx={{ '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!formData.name.trim()}
              >
                {resource ? t('stepContent.resources.actions.updateResource') : t('stepContent.resources.actions.addResource')}
              </Button>
              <Button onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default function ResourcesPage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('resources');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.resources);

  const stepData = getStepData('resources');
  const [resources, setResources] = useState({
    human_resources: stepData.human_resources || [],
    financial_resources: stepData.financial_resources || [],
    technology_resources: stepData.technology_resources || [],
    other_resources: stepData.other_resources || []
  });
  const [isComplete, setIsComplete] = useState(isStepCompleted('resources'));
  const [activeTab, setActiveTab] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const resourceTypes = ['human', 'financial', 'technology', 'other'];
  const resourceKeys = ['human_resources', 'financial_resources', 'technology_resources', 'other_resources'];
  const resourceLabels = [
    t('stepContent.resources.sections.human.title'),
    t('stepContent.resources.sections.financial.title'),
    t('stepContent.resources.sections.technology.title'),
    t('stepContent.resources.sections.other.title')
  ];

  useEffect(() => {
    const data = getStepData('resources');
    setResources({
      human_resources: data.human_resources || [],
      financial_resources: data.financial_resources || [],
      technology_resources: data.technology_resources || [],
      other_resources: data.other_resources || []
    });
    setIsComplete(isStepCompleted('resources'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = (newResources) => {
    setResources(newResources);
    updateStepData('resources', newResources);
  };

  const getCurrentResources = () => {
    return resources[resourceKeys[activeTab]] || [];
  };

  const handleSaveResource = (resourceData) => {
    const currentKey = resourceKeys[activeTab];
    const currentResources = getCurrentResources();
    
    let updatedResources;
    if (editingIndex !== null) {
      updatedResources = currentResources.map((res, index) => 
        index === editingIndex ? resourceData : res
      );
    } else {
      updatedResources = [...currentResources, resourceData];
    }
    
    const newResources = {
      ...resources,
      [currentKey]: updatedResources
    };
    
    updateData(newResources);
    setShowForm(false);
    setEditingIndex(null);
  };

  const handleEditResource = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDeleteResource = (index) => {
    if (window.confirm(t('stepContent.resources.confirmations.deleteResource'))) {
      const currentKey = resourceKeys[activeTab];
      const currentResources = getCurrentResources();
      const updatedResources = currentResources.filter((_, i) => i !== index);
      
      const newResources = {
        ...resources,
        [currentKey]: updatedResources
      };
      
      updateData(newResources);
    }
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('resources', newCompleteState);
    setIsComplete(success);
  };

  const getTotalResources = () => {
    return Object.values(resources).reduce((total, resourceList) => total + resourceList.length, 0);
  };

  const canComplete = getTotalResources() > 0;
  const currentResources = getCurrentResources();

  return (
    <PageContainer spacing={3}>
      <StepPageHeader
        stepNum={5}
        title={t(STEP_LABELS.resources)}
        intro={t('stepContent.resources.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.resources.alerts.requireResources')}
      onAISuggest={handleAISuggest}
        />
      <Paper sx={{ p: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mt: 2 }}
        >
          {resourceLabels.map((label, index) => (
            <Tab
              key={index}
              label={`${label} (${resources[resourceKeys[index]].length})`}
            />
          ))}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingIndex(null);
            setShowForm(true);
          }}
        >
          {t('stepContent.resources.actions.addResource')}
        </Button>
      </Paper>

      {/* Resource Form */}
      {showForm && (
        <Paper sx={{ p: 3 }}>
          <ResourceForm
            resource={editingIndex !== null ? currentResources[editingIndex] : null}
            type={resourceTypes[activeTab]}
            onSave={handleSaveResource}
            onCancel={() => {
              setShowForm(false);
              setEditingIndex(null);
            }}
          />
        </Paper>
      )}

      {/* Resources List */}
      {currentResources.length > 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {resourceLabels[activeTab]} ({currentResources.length})
          </Typography>
          
          <Grid container spacing={3}>
            {currentResources.map((resource, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <ResourceCard
                  resource={resource}
                  index={index}
                  type={resourceTypes[activeTab]}
                  onEdit={handleEditResource}
                  onDelete={handleDeleteResource}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : (
        !showForm && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('stepContent.resources.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stepContent.resources.empty.description')}
            </Typography>
          </Paper>
        )
      )}
    </PageContainer>
  );
}
